// Shared by the browser controller, the Vercel Function, and the unit tests, so this module stays
// free of browser globals and Vite-only APIs.

export const CONTACT_ENDPOINT = "/api/contact";

// A field real visitors never see. Anything typed in it comes from a form-filling bot. The name is
// deliberately not one the browser autofills, so a real visitor using autofill is never flagged.
export const CONTACT_HONEYPOT_FIELD = "subject";

// Bots submit the moment the DOM is parsed; typing three fields takes a human far longer.
export const CONTACT_MIN_ELAPSED_MS = 2_500;
// Beyond a day the timestamp is a replayed payload rather than a slow visitor.
export const CONTACT_MAX_ELAPSED_MS = 86_400_000;

export const CONTACT_LIMITS = {
  name: { min: 2, max: 80 },
  email: { max: 254 },
  message: { min: 12, max: 2_000 },
  // Request bodies are rejected before parsing, so the function never allocates a large payload.
  bodyCharacters: 8_000,
  // Legitimate messages occasionally link one portfolio or brief; link farms do not.
  links: 2,
} as const;

export type ContactFieldName = "name" | "email" | "message";
export type ContactRejection = "invalid" | "spam";

export interface ContactMessage {
  name: string;
  email: string;
  message: string;
}

export type ContactScreening = { ok: true; message: ContactMessage } | { ok: false; rejection: ContactRejection; field?: ContactFieldName };

const EMAIL_PATTERN = /^[^\s@,;:<>()[\]\\"]+@[^\s@.,;:<>()[\]\\"]+(?:\.[^\s@.,;:<>()[\]\\"]+)+$/;
const LINK_PATTERN = /(?:https?:\/\/|www\.|\b[a-z0-9-]+\.(?:com|net|org|ru|xyz|top|shop|info|biz|link|click)\b)/gi;

// Single-line fields must never carry control characters: they end up in mail headers. The
// message keeps line breaks and tabs, nothing else.
function hasControlCharacters(value: string, allowLineBreaks: boolean): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code >= 0x20 && code !== 0x7f) continue;
    if (allowLineBreaks && (code === 0x09 || code === 0x0a || code === 0x0d)) continue;
    return true;
  }
  return false;
}

function readText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function countLinks(value: string): number {
  return value.match(LINK_PATTERN)?.length ?? 0;
}

export function findInvalidContactField(message: ContactMessage): ContactFieldName | null {
  if (message.name.length < CONTACT_LIMITS.name.min || message.name.length > CONTACT_LIMITS.name.max || hasControlCharacters(message.name, false)) return "name";
  if (message.email.length > CONTACT_LIMITS.email.max || !EMAIL_PATTERN.test(message.email)) return "email";
  if (message.message.length < CONTACT_LIMITS.message.min || message.message.length > CONTACT_LIMITS.message.max || hasControlCharacters(message.message, true)) return "message";
  return null;
}

/**
 * Runs every check that does not need a network call: honeypot, dwell time, field shape, and link
 * flooding. `spam` rejections are silent on purpose so bots learn nothing from the response.
 */
export function screenContactRequest(payload: unknown): ContactScreening {
  if (typeof payload !== "object" || payload === null) return { ok: false, rejection: "invalid" };

  const record = payload as Record<string, unknown>;
  if (readText(record[CONTACT_HONEYPOT_FIELD]).length > 0) return { ok: false, rejection: "spam" };

  const elapsedMs = record.elapsedMs;
  if (typeof elapsedMs !== "number" || !Number.isFinite(elapsedMs)) return { ok: false, rejection: "spam" };
  if (elapsedMs < CONTACT_MIN_ELAPSED_MS || elapsedMs > CONTACT_MAX_ELAPSED_MS) return { ok: false, rejection: "spam" };

  const message: ContactMessage = {
    name: readText(record.name).replace(/\s+/g, " "),
    email: readText(record.email),
    message: readText(record.message),
  };

  const field = findInvalidContactField(message);
  if (field) return { ok: false, rejection: "invalid", field };
  if (countLinks(message.name) > 0 || countLinks(message.message) > CONTACT_LIMITS.links) return { ok: false, rejection: "spam" };

  return { ok: true, message };
}
